import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { logger } from './logger';

interface SpotifyTokenData {
  userId: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
  deviceId?: string;
  metadata?: Record<string, any>;
  lastSyncedAt: number;
}

interface SpotifyTokenDB extends DBSchema {
  'spotify-tokens': {
    key: string;
    value: SpotifyTokenData;
    indexes: { 'by-user': string };
  };
}

class SpotifyTokenCache {
  private db: IDBPDatabase<SpotifyTokenDB> | null = null;
  private dbName = 'spotify-token-cache';
  private dbVersion = 1;
  
  async init(): Promise<void> {
    try {
      this.db = await openDB<SpotifyTokenDB>(this.dbName, this.dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('spotify-tokens')) {
            const store = db.createObjectStore('spotify-tokens', { keyPath: 'userId' });
            store.createIndex('by-user', 'userId');
          }
        },
      });
      logger.info('Spotify token cache initialized');
    } catch (error) {
      logger.error('Failed to initialize Spotify token cache', error);
      throw error;
    }
  }

  private async ensureDb(): Promise<IDBPDatabase<SpotifyTokenDB>> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  /**
   * Salva tokens do Spotify no cache local
   */
  async setToken(data: Omit<SpotifyTokenData, 'lastSyncedAt'>): Promise<void> {
    try {
      const db = await this.ensureDb();
      const tokenData: SpotifyTokenData = {
        ...data,
        lastSyncedAt: Date.now()
      };
      
      await db.put('spotify-tokens', tokenData);
      logger.info('Spotify token cached', { userId: data.userId });
    } catch (error) {
      logger.error('Failed to cache Spotify token', error);
      // Não throw error - cache é opcional
    }
  }

  /**
   * Busca tokens do cache local
   */
  async getToken(userId: string): Promise<SpotifyTokenData | null> {
    try {
      const db = await this.ensureDb();
      const token = await db.get('spotify-tokens', userId);
      
      if (!token) {
        logger.debug('No cached token found', { userId });
        return null;
      }

      // Verificar se o token expirou
      const expiresAt = new Date(token.expiresAt);
      const now = new Date();
      
      if (expiresAt <= now) {
        logger.info('Cached token expired, removing', { 
          userId,
          expiresAt: token.expiresAt 
        });
        await this.deleteToken(userId);
        return null;
      }

      // Verificar se o cache está muito antigo (mais de 1 hora)
      const cacheAge = Date.now() - token.lastSyncedAt;
      const oneHour = 60 * 60 * 1000;
      
      if (cacheAge > oneHour) {
        logger.info('Cached token is stale, should resync', { 
          userId,
          cacheAgeMinutes: Math.floor(cacheAge / 60000) 
        });
      }

      logger.debug('Retrieved cached token', { userId });
      return token;
    } catch (error) {
      logger.error('Failed to get cached token', error);
      return null;
    }
  }

  /**
   * Remove token do cache
   */
  async deleteToken(userId: string): Promise<void> {
    try {
      const db = await this.ensureDb();
      await db.delete('spotify-tokens', userId);
      logger.info('Spotify token removed from cache', { userId });
    } catch (error) {
      logger.error('Failed to delete cached token', error);
    }
  }

  /**
   * Atualiza apenas o access token (após refresh)
   */
  async updateAccessToken(
    userId: string, 
    accessToken: string, 
    expiresAt: string
  ): Promise<void> {
    try {
      const db = await this.ensureDb();
      const existing = await db.get('spotify-tokens', userId);
      
      if (!existing) {
        logger.warn('No existing token to update', { userId });
        return;
      }

      const updated: SpotifyTokenData = {
        ...existing,
        accessToken,
        expiresAt,
        lastSyncedAt: Date.now()
      };

      await db.put('spotify-tokens', updated);
      logger.info('Access token updated in cache', { userId });
    } catch (error) {
      logger.error('Failed to update cached access token', error);
    }
  }

  /**
   * Limpa todo o cache (útil para logout global)
   */
  async clearAll(): Promise<void> {
    try {
      const db = await this.ensureDb();
      await db.clear('spotify-tokens');
      logger.info('All cached tokens cleared');
    } catch (error) {
      logger.error('Failed to clear token cache', error);
    }
  }

  /**
   * Verifica se o token está no cache e ainda é válido
   */
  async hasValidToken(userId: string): Promise<boolean> {
    const token = await this.getToken(userId);
    return token !== null;
  }

  /**
   * Obtém estatísticas do cache (para debugging)
   */
  async getCacheStats(): Promise<{
    totalTokens: number;
    oldestSync: number | null;
    newestSync: number | null;
  }> {
    try {
      const db = await this.ensureDb();
      const allTokens = await db.getAll('spotify-tokens');
      
      if (allTokens.length === 0) {
        return { totalTokens: 0, oldestSync: null, newestSync: null };
      }

      const syncTimes = allTokens.map(t => t.lastSyncedAt);
      
      return {
        totalTokens: allTokens.length,
        oldestSync: Math.min(...syncTimes),
        newestSync: Math.max(...syncTimes)
      };
    } catch (error) {
      logger.error('Failed to get cache stats', error);
      return { totalTokens: 0, oldestSync: null, newestSync: null };
    }
  }
}

// Export singleton instance
export const spotifyTokenCache = new SpotifyTokenCache();
